import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useOutletContext, useParams, Navigate, Link } from 'react-router-dom';
import { Plus } from 'lucide-react';
import type { OutletCtx } from '@/layouts/AdminLayout';
import { api, CategorySummary } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { useToast } from '@/components/ui/Toast';
import { AddCategoryDialog } from '@/components/AddCategoryDialog';

export default function SitePage() {
  const { siteSlug } = useParams();
  const { sites } = useOutletContext<OutletCtx>();
  const navigate = useNavigate();
  const { push } = useToast();

  const site = useMemo(() => sites.find((s) => s.slug === siteSlug), [sites, siteSlug]);
  const [categories, setCategories] = useState<CategorySummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);

  const refresh = useCallback(async () => {
    if (!site) return;
    setLoading(true);
    try {
      const list = await api.listCategories(site.id);
      setCategories(list);
      if (list.length > 0) navigate(`/admin/sites/${site.slug}/${list[0].slug}`, { replace: true });
    } catch (e) {
      push(e instanceof Error ? e.message : '카테고리 로드 실패', 'error');
    } finally {
      setLoading(false);
    }
  }, [site, navigate, push]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  if (!site) {
    if (sites.length === 0) return <div className="text-sm text-muted-foreground">사이트를 로드 중…</div>;
    return <Navigate to="/admin" replace />;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{site.name}</h1>
          <div className="text-xs text-muted-foreground">/{site.slug}</div>
        </div>
      </div>

      {loading ? (
        <div className="p-8 text-sm text-muted-foreground">로드 중…</div>
      ) : categories.length === 0 ? (
        <div className="rounded-md border border-dashed p-8 text-center">
          <div className="mb-3 text-sm text-muted-foreground">이 사이트에 카테고리가 없습니다.</div>
          <Button onClick={() => setAddOpen(true)}>
            <Plus className="h-4 w-4" /> 첫 카테고리 추가
          </Button>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {categories.map((c) => (
            <Link
              key={c.id}
              to={`/admin/sites/${site.slug}/${c.slug}`}
              className="rounded-md border bg-card px-3 py-1.5 text-sm hover:bg-accent"
            >
              {c.name}{' '}
              <span className="ml-1 rounded-full bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
                {c.active_item_count}
              </span>
            </Link>
          ))}
          <Button variant="outline" size="sm" onClick={() => setAddOpen(true)}>
            <Plus className="h-4 w-4" /> 카테고리
          </Button>
        </div>
      )}

      <AddCategoryDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        siteId={site.id}
        onCreated={() => {
          void refresh();
        }}
      />
    </div>
  );
}
